import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FaRegUserCircle, FaPowerOff } from "react-icons/fa";
import { userLogout } from "../../redux/loginSlice";
import {
  PARTNER_DASHBOARD,
  PARTNER_CLIENTS,
  PARTNER_COMMISSION,
  INVOICE
} from "../../route/paths";
import {
  persistPartnerName,
  getPersistedPartnerName
} from "./partnerPortalShared";
import { hasRole, ROLE_CLIENT } from "../../utils/accountRoles";
import fundtapLogo from "../../assets/images/logo.png";
import LoginAsUser from "../../components/loginasuser/LoginAsUser";
import "./partnerPortalHeader.scss";

// Partner-portal header (nav redesign v2). Four elements only:
// logo + destinations, the Partner/My-FundTap workspace toggle (dual accounts),
// and an avatar menu (settings + log out). The referral link and "Add client"
// action live on the page content, not here.
export default function PartnerPortalHeader({ partnerName }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Dual accounts (partner + FundTap client) get the workspace toggle.
  const alsoClient = hasRole(ROLE_CLIENT);

  // Close the avatar menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Cache the name so the avatar shows the real initials the instant the header
  // remounts on a tab switch, instead of the initial of a placeholder name until
  // the dashboard fetch resolves.
  useEffect(() => {
    persistPartnerName(partnerName);
  }, [partnerName]);
  const resolvedName = partnerName || getPersistedPartnerName();
  const initials = resolvedName
    ? resolvedName
        .split(/\s+/)
        .map((s) => s.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("")
    : "";

  const navItems = [
    { id: "dashboard", label: "Dashboard", path: PARTNER_DASHBOARD },
    {
      id: "clients",
      label: "Clients",
      path: PARTNER_CLIENTS
    },
    { id: "commission", label: "Commission", path: PARTNER_COMMISSION }
  ];
  const isActive = (item) => {
    if (item.id === "commission")
      return location.pathname.startsWith(PARTNER_COMMISSION);
    if (item.id === "clients")
      return location.pathname.startsWith(PARTNER_CLIENTS);
    return location.pathname.startsWith(PARTNER_DASHBOARD);
  };

  return (
    <>
      {/* Staff impersonation banner — renders only when acting as this partner,
          giving a "return to admin" link inside the portal. */}
      <LoginAsUser />
      <div className="partner-portal-header-accent" />
      <header className="partner-portal-header">
        {/* Left: logo + destinations */}
        <div className="partner-portal-header__brand-nav">
          <div className="partner-portal-header__logo-wrap">
            <img
              src={fundtapLogo}
              alt="FundTap"
              onClick={() => navigate(PARTNER_DASHBOARD)}
              className="partner-portal-header__logo"
            />
          </div>
          <nav className="partner-portal-header__nav">
            {navItems.map((n) => (
              <button
                key={n.id}
                onClick={() => navigate(n.path)}
                className={`partner-portal-header__nav-item${
                  isActive(n) ? " is-active" : ""
                }`}
              >
                {n.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Right: workspace toggle + avatar menu */}
        <div className="partner-portal-header__actions">
          {alsoClient && (
            <div className="partner-portal-header__workspace-toggle">
              <button
                type="button"
                className="partner-portal-header__workspace-btn is-current"
              >
                Partner
              </button>
              <button
                type="button"
                onClick={() => navigate(INVOICE)}
                title="Switch to your FundTap customer account"
                className="partner-portal-header__workspace-btn"
              >
                My FundTap
              </button>
            </div>
          )}

          <div className="partner-portal-header__menu-wrap" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="partner-portal-header__avatar-btn"
            >
              <span className="partner-portal-header__avatar">
                {initials || (
                  <FaRegUserCircle
                    className="partner-portal-header__avatar-icon"
                    aria-hidden
                  />
                )}
              </span>
              <span className="partner-portal-header__caret">▾</span>
            </button>

            {menuOpen && (
              <div className="partner-portal-header__menu">
                <div className="partner-portal-header__menu-head">
                  <div className="partner-portal-header__menu-name">
                    {partnerName || "Partner"}
                  </div>
                  <div className="partner-portal-header__menu-role">
                    Referral partner
                  </div>
                </div>
                <button
                  onClick={() => dispatch(userLogout())}
                  className="partner-portal-header__menu-item"
                >
                  <FaPowerOff aria-hidden />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}

PartnerPortalHeader.propTypes = {
  partnerName: PropTypes.string
};
